import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import {
  IconComponent,
  ageCategoryadult,
  ageCategorychild,
  ageCategoryteen,
  chevronRight,
  circleNotch,
  cross,
  damage,
  provideIcons
} from '../../../../shared/icons';
import { FLY_IN_OUT_FROM_TOP_ANIMATION } from '../../../../core/animations/FLY_IN_OUT_FROM_TOP_ANIMATION';
import { CurrencyPipe, DOCUMENT, NgClass } from '@angular/common';
import { CurrencyPipe as AlpiCurrencyPipe } from '../../../../core/pipes/currency.pipe';
import { CartEventType, CartService } from '../../services/cart';
import { CurrencyService } from '../../../../core/services/currency.service';
import { ResolutionService } from '../../../../core/services/resolution.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Addon, Person, Product } from '../../services/entities';
import { ActivatedRoute, Router } from '@angular/router';
import { PartnerLinkService } from '../../../../core/services/partner-link.service';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { ProductService } from '../../services/product/product.service';
import { TagManagerService } from '../../../../core/services/tag-manager.service';
import { ToggleComponent } from '../toggle/toggle.component';
import { AlpitranslatePipe } from '../../../../core/pipes/alpitranslate.pipe';
import { PlatformassetPipe } from '../../../../../../projects/platform-manager/src/public_api';

@Component({
  selector: 'app-insurance-upselling',
  imports: [
    IconComponent,
    ToggleComponent,
    AlpiCurrencyPipe,
    NgClass,
    CurrencyPipe,
    AlpitranslatePipe,
    PlatformassetPipe
  ],
  providers: [
    provideIcons({
      ageCategoryadult,
      ageCategoryteen,
      ageCategorychild,
      circleNotch,
      damage,
      cross,
      chevronRight
    })
  ],
  animations: [FLY_IN_OUT_FROM_TOP_ANIMATION],
  templateUrl: './insurance-upselling.component.html',
  styleUrl: './insurance-upselling.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InsuranceUpsellingComponent implements OnInit, OnDestroy {
  @HostBinding('@flyInOutFromTop') flyInOutTrigger = 'in';

  protected isSmallerThanLarge = this.resolutionService.isSmallerThanLargeDevice();
  protected cart = this.cartService.cart;
  protected products: Product[] = [];
  readonly amountWithCurrency = {
    EUR: { min: '400 €', max: '800 €' },
    USD: { min: '450 $', max: '900 $' },
    CHF: { min: 'Fr.420', max: 'Fr.840' },
    GBP: { min: '£340', max: '£680' },
    CZK: { min: 'Kč 10,100', max: 'Kč 20,200' },
    PLN: { min: '1,800 zł', max: '3,700 zł' }
  };

  constructor(
    private cartService: CartService,
    private productService: ProductService,
    protected currencyService: CurrencyService,
    protected resolutionService: ResolutionService,
    protected translateService: TranslateService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected localizeRouterService: LocalizeRouterService,
    protected partnerLinkService: PartnerLinkService,
    protected tagManagerService: TagManagerService,
    private renderer2: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit() {
    if (this.document) {
      this.renderer2.setStyle(this.document.body, 'overflow', 'hidden');
    }
    this.productService
      .getProducts(+this.route.parent.snapshot.queryParamMap.get('shopId'))
      .subscribe(products => {
        this.products = products.products;
        this.cdr.markForCheck();
      });

    this.cartService.cart$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      this.cart = event.cart;
      this.cdr.markForCheck();
    });

    this.resolutionService.sizeReferenceChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isSmallerThanLarge = this.resolutionService.isSmallerThanLargeDevice();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    if (this.document) {
      this.renderer2.setStyle(this.document.body, 'overflow', 'initial');
    }
  }

  closeMe() {
    this.router.navigate([{ outlets: { modal: null } }], {
      relativeTo: this.route.parent,
      queryParamsHandling: 'merge'
    });
  }

  continueToCheckout() {
    const route = this.partnerLinkService
      .getUrlReplacingPartnerAfterPrefix(
        this.localizeRouterService.translateRoute('/CHECKOUT.PAYMENT/CHECKOUT.CHECKOUT'),
        this.localizeRouterService.translateRoute('/CHECKOUT.PAYMENT')
      )
      .replace('partner-', 'partner-/');
    this.router.navigate([route], { queryParamsHandling: 'merge', state: { fromProductSelection: true } });
  }

  isInCart(product: Product): boolean {
    return product.addons.some(addon => addon.definitionId === 3);
  }

  changeInsuranceAddon(product: Product, person: Person) {
    this.cartService.updateCart(CartEventType.productRemoved, person, product);
    if (product.addons.find(a => a.classname === 'insurance')) {
      // remove addon
      product.addons.splice(
        product.addons.findIndex(a => a.classname === 'insurance'),
        1
      );
    } else {
      // add addon
      const p = this.products.find(p => p.definitionId === product.definitionId);
      if (p) {
        product.addons.push(p.addons.find(a => a.classname === 'insurance'));
      }
    }

    this.tagManagerService.push(
      {
        event: 'upsellingModalOption',
        ecommerce: {
          checkout: {
            actionField: { step: 0, option: 'AlpinGuaranty' }
          }
        }
      },
      'upsellingModalOption'
    );

    this.cartService.updateCart(CartEventType.productUpdated, person, product);
  }

  insurableAddonFor(product: Product): Addon | null {
    const prod = this.products.find(p => p.definitionId === product.definitionId);
    if (prod) {
      return prod.addons.find(addon => addon.classname === 'insurance');
    }
    return null;
  }

  insuranceForAll(): number | null {
    if (this.cart && this.products) {
      let total = 0;
      this.cart.persons.forEach(person => {
        person.productEntities.forEach(product => {
          const insuranceAddon = this.insurableAddonFor(product);
          if (insuranceAddon) {
            total += insuranceAddon.getOnlinePrice(
              this.cart.getDurationInDays(),
              person,
              product,
              this.cart.euroBasedRates,
              this.currencyService.actualCurrency
            ).amount;
          }
        });
      });
      return total / 100;
    }

    return null;
  }
}
